/**
 * Hook for fetching users associated with a specific buyer
 * Provides buyer user data and loading states
 */

import type { BuyerUserSelectOption } from '@choco-admin/types';
import { useCallback, useMemo } from 'react';

import { useGetBuyerUsersLazyQuery } from '../../../generated';
import { mapToBuyerUserSelectOption } from '../utils/createChatMapper';

export interface UseGetBuyerUsersResponse {
  buyerUsers: BuyerUserSelectOption[];
  loading: boolean;
  error: Error | null;
  getBuyerUsers: (buyerId: string) => Promise<BuyerUserSelectOption[]>;
}

export const useGetBuyerUsers = (): UseGetBuyerUsersResponse => {
  const [getBuyerUsersQuery, { data, loading, error }] =
    useGetBuyerUsersLazyQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    });

  const getBuyerUsers = useCallback(
    async (buyerId: string): Promise<BuyerUserSelectOption[]> => {
      if (!buyerId?.trim()) {
        return [];
      }
      const result = await getBuyerUsersQuery({
        variables: {
          buyerEntityId: buyerId,
          first: 150,
        },
      });
      if (!result.data?.buyer?.users?.edges) {
        return [];
      }
      return result.data.buyer.users.edges
        .map(mapToBuyerUserSelectOption)
        .filter(Boolean) as BuyerUserSelectOption[];
    },
    [getBuyerUsersQuery]
  );

  const buyerUsers = useMemo(() => {
    if (!data?.buyer?.users?.edges) {
      return [];
    }

    return data.buyer.users.edges
      .map(mapToBuyerUserSelectOption)
      .filter(Boolean) as BuyerUserSelectOption[];
  }, [data]);

  return {
    buyerUsers,
    loading,
    error: error || null,
    getBuyerUsers,
  };
};
